import React, { useEffect, useState } from 'react'

import classNames from 'classnames'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../SelectDropdown/SelectDropdown'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  resetCustomEmailFilter,
  selectEmailFilters,
  selectSelectedQuickEmailFilter,
  setEditingQuickEmailFilter,
  setEmailFilterModalMode,
  setSelectedQuickEmailFilter,
  setShowEmailFilterModal,
} from '../../store/emailHub/emailFilters'
import { OverflowTooltip } from '../../apps/professional-app/fa/common/OverflowTooltip/OverflowTooltip'
import { TagElement } from '../Tag/Tag'
import { Button } from '../Button/Button'
import { useFetchEmailThreads } from '../../views/EmailV2/hooks'
import { selectEmailThreadsLoading } from '../../store/emailHub/emailThreads'

export const EmailQuickFilter: React.FC = () => {
  const dispatch = useAppDispatch()

  const emailFilters = useAppSelector(selectEmailFilters)
  const selectedQuickEmailFilter = useAppSelector(selectSelectedQuickEmailFilter)
  const emailThreadsLoading = useAppSelector(selectEmailThreadsLoading)

  const { fetchEmailThreads } = useFetchEmailThreads()

  const [quickFilterOptions, setQuickFilterOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    setQuickFilterOptions(
      emailFilters.map((filter) => {
        return { id: filter.id, label: filter.name }
      }),
    )
  }, [emailFilters])

  const handleQuickFilterSelected = async (option: SelectOption) => {
    const quickFilter = emailFilters.find((filter) => filter.id === option.id)
    if (!quickFilter) {
      return
    }
    dispatch(setSelectedQuickEmailFilter(quickFilter))
    await fetchEmailThreads({ filterId: quickFilter.id })
  }

  const handleQuickFilterEdited = (optionId: string) => {
    const quickFilter = emailFilters.find((filter) => filter.id === optionId)
    if (!quickFilter) {
      return
    }
    dispatch(setEmailFilterModalMode('edit'))
    dispatch(setEditingQuickEmailFilter(quickFilter))
    dispatch(setShowEmailFilterModal(true))
  }

  const handleCreateNewFilter = () => {
    dispatch(resetCustomEmailFilter())
    dispatch(setEmailFilterModalMode('new'))
    dispatch(setShowEmailFilterModal(true))
  }

  const QuickFilterItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick }) => {
    const quickFilter = emailFilters.find((filter) => filter.id === optionId)
    return (
      <CustomDropdownItemOption role='button' onClick={onClick}>
        <div className='d-flex align-items-center gap-2'>
          <OverflowTooltip>{optionLabel}</OverflowTooltip>
          {quickFilter && quickFilter.isDefault ? <span className='zocks-text-primary lead-extra-small'>Default</span> : null}
          {quickFilter ? (
            <>
              <TagElement
                className={classNames('lead-extra-small zocks-py-0.125 px-1 text-uppercase zocks-text-white', {
                  'zocks-bg-color-primary': quickFilter.type === 'system',
                  'zocks-bg-color-gray-900': quickFilter.type !== 'system',
                })}
              >
                {quickFilter.type === 'system' ? 'zocks' : 'custom'}
              </TagElement>
              {quickFilter.type === 'user' ? (
                <div className='quick-filter-item-option__edit-button' onClick={() => handleQuickFilterEdited(optionId)}>
                  <i className='bi bi-pencil' />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </CustomDropdownItemOption>
    )
  }

  const CreateNewDropdownFooter = () => {
    return (
      <Button className='quick-filter-dropdown__create-new-button mt-3 button-medium w-100' variant='dashedButton' onClick={handleCreateNewFilter}>
        + Create new
      </Button>
    )
  }

  return (
    <div className='quick-filter-dropdown-container'>
      <div className='lead-extra-small mb-1 d-flex gap-1'>
        <i className='bi bi-stopwatch' />
        Quick filter
      </div>
      <SelectDropdown
        className={classNames('py-2 zocks-px-0.75', { 'zocks-border-color-primary': selectedQuickEmailFilter })}
        options={quickFilterOptions}
        onOptionSelected={handleQuickFilterSelected}
        initialSelectedOption={quickFilterOptions.find((option) => option.id === selectedQuickEmailFilter?.id) ?? null}
        DropDownItemOptionComponent={QuickFilterItemOption}
        DropdownFooterComponent={CreateNewDropdownFooter}
        placeholder='Select quick filter'
        disabled={emailThreadsLoading}
      />
    </div>
  )
}
